import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSupportSettings, updateSupportSettings, setInformationText, setFeedbackLink } from '../../slice/supportSettingsSlice';

const Support = () => {
  const dispatch = useDispatch();
  const { data: userData } = useSelector((state) => state.admin);
  const { information_text, feedback_link, loading, error } = useSelector((state) => state.supportSettings);
  
  const [isEditing, setIsEditing] = useState(false);
  const [editInfoText, setEditInfoText] = useState('');
  const [editFeedbackLink, setEditFeedbackLink] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const isSuperAdmin = userData?.user_role === 'SUPER_ADMIN';

  useEffect(() => {
    dispatch(fetchSupportSettings());
  }, [dispatch]);

  useEffect(() => {
    setEditInfoText(information_text);
    setEditFeedbackLink(feedback_link);
  }, [information_text, feedback_link]);

  const handleSave = async () => {
    if (!isSuperAdmin) return;
    
    setIsSaving(true);
    try {
      await dispatch(updateSupportSettings({
        information_text: editInfoText,
        feedback_link: editFeedbackLink
      })).unwrap();
      
      setIsEditing(false);
      dispatch(fetchSupportSettings()); // Refresh data
    } catch (error) {
      console.error('Failed to update support settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditInfoText(information_text);
    setEditFeedbackLink(feedback_link);
    setIsEditing(false);
  };

  const handleEdit = () => {
    if (!isSuperAdmin) return;
    setIsEditing(true);
  };

  return (
    <div className="mb-16 pt-5 mt-8 mx-auto px-4 sm:px-6 rounded lg:px-8">
      <div className="pl-32 pr-40">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-semibold mb-6">Support</h1>
          </div>
        </div>

        {/* Support Content */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-yellow"></div>
          </div>
        ) : (
        <div className="space-y-6">
          <div className="bg-custom-white_light rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Informationen</h2>
              {isSuperAdmin && (
                <div className="flex space-x-2">
                  {!isEditing ? (
                    <button
                      onClick={handleEdit}
                      className="px-3 py-1 text-sm bg-custom-yellow text-white rounded hover:bg-yellow-500 transition-colors"
                    >
                      Bearbeiten
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        {isSaving ? 'Speichern...' : 'Speichern'}
                      </button>
                      <button
                        onClick={handleCancel}
                        className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                      >
                        Abbrechen
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            
            <div className="space-y-3">
              {isEditing && isSuperAdmin ? (
                <textarea
                  value={editInfoText}
                  onChange={(e) => setEditInfoText(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-yellow focus:border-transparent transition-colors duration-200"
                  rows={6}
                  placeholder="Geben Sie hier die Support-Informationen ein..."
                />
              ) : (
                <div className="p-4">
                  <p className="text-gray-700 whitespace-pre-wrap text-sm font-medium leading-relaxed">
                    {information_text || 'Hier können Sie Informationen über unseren Support finden.'}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-custom-white_light rounded-lg border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Support-Zeiten</h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600 text-sm font-medium">Montag - Freitag</span>
                <span className="font-medium text-gray-900 text-sm">07:00 - 17:00</span>
              </div>
            </div>
          </div>

          <div className="bg-custom-white_light rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Support- und Feedback-Anfragen</h2>
              {isSuperAdmin && (
                <div className="flex space-x-2">
                  {!isEditing ? (
                    <button
                      onClick={handleEdit}
                      className="px-3 py-1 text-sm bg-custom-yellow text-white rounded hover:bg-yellow-500 transition-colors"
                    >
                      Bearbeiten
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        {isSaving ? 'Speichern...' : 'Speichern'}
                      </button>
                      <button
                        onClick={handleCancel}
                        className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                      >
                        Abbrechen
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            
            <div className="space-y-3">
              {isEditing && isSuperAdmin ? (
                <input
                  type="url"
                  value={editFeedbackLink}
                  onChange={(e) => setEditFeedbackLink(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-yellow focus:border-transparent transition-colors duration-200"
                  placeholder="https://example.com/feedback"
                />
              ) : (
                <div className="p-4">
                  <a
                    href={feedback_link || 'https://example.com/feedback'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
                  >
                    {feedback_link || 'https://example.com/feedback'}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            Fehler: {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default Support;
